import React, { useState } from 'react';
import { apiFetch } from '../lib/api';

// @@SHOPIFY_TAX_REPORT_v71@@
// Portfolio-wide sales tax by region across every client's Shopify stores.
// One month at a time; runs server-side sequentially so it can take a bit.
function defaultMonth() {
  const d = new Date();
  // Default to the *previous* full month.
  d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() - 1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export default function ShopifyTax() {
  const [month, setMonth] = useState(defaultMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  async function run() {
    setBusy(true); setMsg(''); setData(null);
    try {
      const res = await apiFetch(`/api/clients/shopify-tax-report?period_start=${month}`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setMsg(d.error || 'Could not build the report.');
        return;
      }
      setData(await res.json());
    } catch (e) {
      setMsg('Could not reach the server.');
    } finally { setBusy(false); }
  }

  function money(n) {
    const v = Number(n) || 0;
    return v.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  }

  function exportCsv() {
    if (!data) return;
    const rows = [['Client', 'Store', 'Region', 'Gross', 'Net Sales', 'Shipping', 'Tax']]; // @@SHOPIFY_TAX_NET_v73@@ // @@SHOPIFY_TAX_GROSS_v72@@
    for (const s of data.stores) {
      if (s.error) { rows.push([s.client_name, s.label, `(${s.error})`, '', '', '', '']); continue; }
      if (!s.regions.length) { rows.push([s.client_name, s.label, '(none)', '0', '0', '0', '0']); continue; }
      for (const r of s.regions) rows.push([s.client_name, s.label, r.region, String(r.gross || 0), String(r.net || 0), String(r.shipping || 0), String(r.taxes)]);
    }
    rows.push([]);
    rows.push(['— COMBINED BY REGION —']);
    for (const r of data.by_region_combined) rows.push(['', '', r.region, String(r.gross || 0), String(r.net || 0), String(r.shipping || 0), String(r.taxes)]);
    rows.push([]);
    rows.push(['GRAND TOTAL', '', '', String(data.grand_gross || 0), String(data.grand_net || 0), String(data.grand_shipping || 0), String(data.grand_total)]);

    const esc = (c) => {
      const s = String(c == null ? '' : c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `shopify-sales-tax-${data.period_start.slice(0, 7)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Sales Tax Report</h1>
        <p>Tax collected by region across every connected Shopify store.</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Period</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="hq-input" type="month" value={month}
              onChange={(e) => setMonth(e.target.value)} style={{ minWidth: 160 }} />
            <button className="hq-btn" onClick={run} disabled={busy}>
              {busy ? 'Running…' : 'Run report'}
            </button>
            {data && <button className="hq-btn" onClick={exportCsv}>Export CSV</button>}
          </div>
        </div>
        {busy && (
          <div style={{ padding: '16px 20px', color: 'var(--hq-muted)', fontSize: 13 }}>
            Querying each store one at a time (throttle-safe) — this can take a moment for many stores.
          </div>
        )}
        {msg && <p className="hq-msg" style={{ padding: '0 20px 16px' }}>{msg}</p>}
      </div>

      {data && (
        <>
          {/* @@SHOPIFY_TAX_NET_v73@@ */}
          <div className="hq-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="hq-stat">
              <div className="hq-k">Grand Gross Sales</div>
              <div className="hq-v">{money(data.grand_gross)}</div>
            </div>
            <div className="hq-stat">
              <div className="hq-k">Grand Net Sales</div>
              <div className="hq-v">{money(data.grand_net)}</div>
            </div>
            <div className="hq-stat">
              <div className="hq-k">Grand Shipping</div>
              <div className="hq-v">{money(data.grand_shipping)}</div>
            </div>
            <div className="hq-stat">
              <div className="hq-k">Grand Total Tax</div>
              <div className="hq-v">{money(data.grand_total)}</div>
            </div>
          </div>

          <div className="hq-panel" style={{ marginBottom: 20 }}>
            <div className="hq-panel-head"><h2>By store &amp; region</h2></div>
            <table className="hq-table">
              <thead>
                <tr><th>Client</th><th>Store</th><th>Region</th><th style={{ textAlign: 'right' }}>Gross</th><th style={{ textAlign: 'right' }}>Net Sales</th><th style={{ textAlign: 'right' }}>Shipping</th><th style={{ textAlign: 'right' }}>Tax</th></tr>
              </thead>
              <tbody>
                {data.stores.map((s) => {
                  if (s.error) {
                    return (
                      <tr key={s.store_id}>
                        <td>{s.client_name}</td>
                        <td>{s.label}</td>
                        <td colSpan={5} style={{ color: '#e8c15a', fontSize: 13 }}>⚠ {s.error}</td>
                      </tr>
                    );
                  }
                  if (!s.regions.length) {
                    return (
                      <tr key={s.store_id}>
                        <td>{s.client_name}</td>
                        <td>{s.label}</td>
                        <td colSpan={5} style={{ color: 'var(--hq-muted)', fontSize: 13 }}>No sales</td>
                      </tr>
                    );
                  }
                  return s.regions.map((r, idx) => (
                    <tr key={`${s.store_id}-${idx}`}>
                      <td>{idx === 0 ? s.client_name : ''}</td>
                      <td>{idx === 0 ? s.label : ''}</td>
                      <td>{r.region}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.gross)}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.net)}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.shipping)}</td>
                      <td style={{ textAlign: 'right' }}>{money(r.taxes)}</td>
                    </tr>
                  )).concat(
                    <tr key={`${s.store_id}-subtotal`} style={{ background: 'rgba(255,255,255,.02)' }}>
                      <td colSpan={3} style={{ textAlign: 'right', color: 'var(--hq-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        {s.label} subtotal
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(s.gross_total)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(s.net_total)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(s.shipping_total)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(s.taxes_total)}</td>
                    </tr>
                  );
                })}
                {data.stores.length === 0 && (
                  <tr><td className="hq-empty" colSpan={7}>No Shopify stores connected.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="hq-panel">
            <div className="hq-panel-head"><h2>Combined by region</h2></div>
            <table className="hq-table">
              <thead>
                <tr><th>Region</th><th style={{ textAlign: 'right' }}>Gross</th><th style={{ textAlign: 'right' }}>Net Sales</th><th style={{ textAlign: 'right' }}>Shipping</th><th style={{ textAlign: 'right' }}>Tax</th></tr>
              </thead>
              <tbody>
                {data.by_region_combined.map((r) => (
                  <tr key={r.region}>
                    <td>{r.region}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.gross)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.net)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.shipping)}</td>
                    <td style={{ textAlign: 'right' }}>{money(r.taxes)}</td>
                  </tr>
                ))}
                <tr style={{ background: 'rgba(255,255,255,.02)' }}>
                  <td style={{ fontWeight: 700 }}>Grand total</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(data.grand_gross)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(data.grand_net)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(data.grand_shipping)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(data.grand_total)}</td>
                </tr>
                {data.by_region_combined.length === 0 && (
                  <tr><td className="hq-empty" colSpan={5}>No tax data.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
